import React, { useEffect, useRef } from 'react';
import * as echarts from 'echarts';
import useSystemTheme from './useSystemTheme';

const FourPieCharts = ({ data }) => {
    const chartRef = useRef(null);

    const theme = useSystemTheme();
    const textColor = theme === 'dark' ? '#eee' : '#666';


    useEffect(() => {
        if (!data || data.length === 0) return;

        const chart = echarts.init(chartRef.current);
        const isMobile = window.innerWidth <= 668;

        // Remove invalid entries
        data = data.filter(item => item.channels && item.channels.trim() !== '');

        const channelsToGroup = [
            "Display", "Facebook", "Media", "Others", "Promotional", "Referral", "Cpc",
            "Safefra", "Shopzilla_cse", "Unspecified", "Youtube_Video", "Social_Other", "Affiliate"
        ];

        let transformedData = [...data];

        if (isMobile) {
            const grouped = {};

            transformedData.forEach(item => {
                const channelKey = channelsToGroup.includes(item.channels) ? 'Other' : item.channels;

                if (!grouped[channelKey]) {
                    grouped[channelKey] = {
                        channels: channelKey,
                        attribution_first_click_heuristic: 0,
                        attribution_last_click_heuristic: 0,
                        attribution_markov_algorithmic: 0,
                        attribution_shapley_size4_conv_rate_algorithmic: 0
                    };
                }

                grouped[channelKey].attribution_first_click_heuristic += item.attribution_first_click_heuristic || 0;
                grouped[channelKey].attribution_last_click_heuristic += item.attribution_last_click_heuristic || 0;
                grouped[channelKey].attribution_markov_algorithmic += item.attribution_markov_algorithmic || 0;
                grouped[channelKey].attribution_shapley_size4_conv_rate_algorithmic += item.attribution_shapley_size4_conv_rate_algorithmic || 0;
            });

            transformedData = Object.values(grouped);
        }

        const channels = [...new Set(transformedData.map(item => item.channels))];

        const getPieData = (metricKey) =>
            transformedData.map(item => ({
                name: item.channels,
                value: Math.round(item[metricKey] || 0)
            })).filter(d => d.value > 0);

        const pieSeries = [
            {
                name: 'First Click',
                data: getPieData('attribution_first_click_heuristic'),
                center: ['20%', '30%']
            },
            {
                name: 'Last Click',
                data: getPieData('attribution_last_click_heuristic'),
                center: ['70%', '30%']
            },
            {
                name: 'Data Attribution 1',
                data: getPieData('attribution_markov_algorithmic'),
                center: ['20%', '75%']
            },
            {
                name: 'Data Attribution 2',
                data: getPieData('attribution_shapley_size4_conv_rate_algorithmic'),
                center: ['70%', '75%']
            }
        ];


        const series = pieSeries.map(pie => ({
            type: 'pie',
            name: pie.name,
            radius: ['25%', '40%'],
            center: pie.center,
            label: {
                show: false,
                position: 'center',
                color: textColor,
            },
            emphasis: {
                label: {
                    show: true,
                    formatter: '{b}\n${c}',
                    fontSize: 16,
                    fontWeight: 'bold',
                    color: textColor
                }
            },
            labelLine: {
                show: false
            },
            data: pie.data
        }));

        const option = {
            title: [
                { text: 'First Click', left: '10%', top: '5%', textStyle: { color: textColor, fontSize: 14 } },
                { text: 'Last Click', left: '60%', top: '5%', textStyle: { color: textColor, fontSize: 14 } },
                { text: 'Data Attribution 1', left: '10%', top: '50%', textStyle: { color: textColor, fontSize: 14 } },
                { text: 'Data Attribution 2', left: '60%', top: '50%', textStyle: { color: textColor, fontSize: 14 } },
            ],
            tooltip: {
                trigger: 'item',
                formatter: '{a} <br/>{b}: {c} ({d}%)'
            },
            legend: {
                type: 'scroll',
                orient: 'horizontal',
                right: 10,
                top: 'top',
                data: channels,
                textStyle: {
                    color: textColor, // Use theme-based legend color
                },
            },
            color: [
                '#e6194B', '#3cb44b', '#ffe119', '#4363d8', '#f58231',
                '#911eb4', '#46f0f0', '#f032e6', '#bcf60c', '#fabebe',
                '#008080', '#e6beff', '#9A6324', '#fffac8', '#800000',
                '#aaffc3', '#808000', '#ffd8b1', '#000075', '#808080'
            ],
            series
        };

        chart.setOption(option);

        const resize = () => chart.resize();
        window.addEventListener('resize', resize);

        return () => {
            chart.dispose();
            window.removeEventListener('resize', resize);
        };
    }, [data]);

    return <div ref={chartRef} style={{ width: '100%', height: '600px' }} />;
};

export default FourPieCharts;
